import { isStagingTestMode, stagingEmailMessage } from '../lib/stagingEnvironment'
import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'

function safeNextPath(search) {
  const params = new URLSearchParams(search || '')
  const next = params.get('next') || '/'
  if (!next.startsWith('/') || next.startsWith('//')) return '/'
  if (next.includes('\\') || /^[a-z][a-z0-9+.-]*:/i.test(next)) return '/'
  return next
}

export default function AuthPage() {
  const [tab, setTab] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [workshopName, setWorkshopName] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showForgot, setShowForgot] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const { signIn, signUp } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null); setSuccess(null); setLoading(true)
    if (tab === 'login') {
      const { error } = await signIn(email, password)
      if (error) setError(error.message)
      else navigate(safeNextPath(location.search), { replace: true })
    } else {
      if (!workshopName.trim()) { setError('Workshop name required.'); setLoading(false); return }
      const { error } = await signUp(email, password, workshopName, fullName)
      if (error) setError(error.message)
      else setSuccess('Account created — check your email to confirm, then log in.')
    }
    setLoading(false)
  }

  async function handleForgot(e) {
    e.preventDefault()
    if (!forgotEmail.trim()) return
    if (isStagingTestMode()) { setError(stagingEmailMessage); return }
    setForgotLoading(true)
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
      redirectTo: `${window.location.origin}/reset-password`
    })
    setForgotLoading(false)
    if (error) setError(error.message)
    else setForgotSent(true)
  }

  if (showForgot) {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div className="auth-logo">Powered by <span>Rebound</span></div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Reset password</div>
          <p className="text-muted fs12" style={{ marginBottom: 20 }}>
            Enter your email and we'll send a reset link.
          </p>
          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          {forgotSent ? (
            <>
              <div className="alert alert-success" style={{ marginBottom: 20 }}>
                Reset link sent — check your email.
              </div>
              <button className="btn btn-ghost btn-full" onClick={() => { setShowForgot(false); setForgotSent(false); setError(null) }}>
                Back to login
              </button>
            </>
          ) : (
            <form onSubmit={handleForgot}>
              <div className="field" style={{ marginBottom: 20 }}>
                <label>Email</label>
                <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your@email.com" required />
              </div>
              <button className="btn btn-primary btn-full" type="submit" disabled={forgotLoading}>
                {forgotLoading ? <><span className="spinner" />&nbsp;Sending…</> : 'Send Reset Link'}
              </button>
              <button type="button" className="btn btn-ghost btn-full" style={{ marginTop: 10 }} onClick={() => { setShowForgot(false); setError(null) }}>
                Back to login
              </button>
            </form>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">Powered by <span>Rebound</span></div>
        <div className="auth-tabs">
          <div className={`auth-tab ${tab === 'login' ? 'active' : ''}`} onClick={() => { setTab('login'); setError(null) }}>Log In</div>
          <div className={`auth-tab ${tab === 'signup' ? 'active' : ''}`} onClick={() => { setTab('signup'); setError(null) }}>Create Account</div>
        </div>
        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}
        <form onSubmit={handleSubmit}>
          {tab === 'signup' && (
            <>
              <div className="field"><label>Workshop Name</label><input value={workshopName} onChange={e => setWorkshopName(e.target.value)} placeholder="e.g. Torque Collective" required /></div>
              <div className="field"><label>Your Name</label><input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Darren Simpkin" /></div>
            </>
          )}
          <div className="field"><label>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} required /></div>
          <div className="field" style={{ marginBottom: tab === 'login' ? 8 : 20 }}>
            <label>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} placeholder="Min. 8 characters" />
          </div>
          {tab === 'login' && (
            <div style={{ marginBottom: 20, textAlign: 'right' }}>
              <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '2px 0', fontSize: 12, border: 'none', color: 'var(--muted)' }} onClick={() => { setShowForgot(true); setForgotEmail(email); setError(null) }}>
                Forgot password?
              </button>
            </div>
          )}
          <button className="btn btn-primary btn-full" type="submit" disabled={loading}>
            {loading ? <><span className="spinner" />&nbsp;{tab === 'login' ? 'Logging in…' : 'Creating account…'}</> : tab === 'login' ? 'Log In' : 'Create Account'}
          </button>
        </form>
        <div className="legal-footer" style={{ marginTop: 20 }}>
          <a onClick={() => window.open('/legal', '_blank')}>Terms of Service</a>
          {' · '}
          <a onClick={() => window.open('/legal', '_blank')}>Privacy Policy</a>
        </div>
      </div>
    </div>
  )
}
